// src/pages/PublicChat.jsx
import { useEffect, useState, useRef, useContext } from "react";
import { useParams } from "react-router-dom";
import { startConversation, sendMessage, getHistory } from "../api/webchat";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Helmet } from "react-helmet";
import { LanguageContext } from "../context/LanguageContext";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import frFlag from "../images/flags/french-flag-icon.png";
import ukFlag from "../images/flags/uk-flag-icon.png";

function PublicChatBase({ variant = "share" }) {
  const { tenantId, agentId } = useParams();
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]); // {role, content}[]
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [email, setEmail] = useState("");
  const [startRequested, setStartRequested] = useState(false);
  const endRef = useRef(null);
  const { translate, language, setLanguage } = useContext(LanguageContext);
  const [hasShare, setHasShare] = useState(() => {
    try {
      return Boolean(new URLSearchParams(window.location.search).get("k"));
    } catch {
      return false;
    }
  });
  const [agentMeta, setAgentMeta] = useState({ name: "", presentation: "" });
  const isEmbed = variant === "embed";

  // Fetch agent meta
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!tenantId || !agentId) return;
        const ref = doc(db, "tenants", String(tenantId), "agents", String(agentId));
        const snap = await getDoc(ref);
        if (!snap.exists()) return;
        const d = snap.data() || {};
        if (!cancelled)
          setAgentMeta({
            name: d.companyName || d.name || "",
            presentation: d.presentation || d.description || "",
          });
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, agentId]);

  // Start conversation
  useEffect(() => {
    if (!tenantId || !agentId || !startRequested) return;
    let cancelled = false;

    const params = new URLSearchParams(window.location.search);
    const hasShareParam = Boolean(params.get("k"));
    setHasShare(hasShareParam);
    const site = hasShareParam ? "share_link" : params.get("site") || document.referrer || "unknown";
    let channel = params.get("ch") || (window.self !== window.top ? "iframe" : "url");
    if (!["widget", "iframe", "url"].includes(channel)) channel = "url";

    (async () => {
      setStarting(true);
      try {
        const key = `tm:conv:${tenantId}:${agentId}:${(email || "").trim().toLowerCase()}`;
        let convId = localStorage.getItem(key) || null;

        if (!convId) {
          const hints = { source: hasShareParam ? "share" : "public", site, channel, resume: true };
          if (email.trim()) hints.email = email.trim();
          const resp = await startConversation(tenantId, agentId, hints);
          convId = resp?.conversationId || null;
          if (convId) localStorage.setItem(key, convId);
        }

        if (!cancelled && convId) {
          setConversationId(convId);
          try {
            const h = await getHistory(tenantId, agentId, convId);
            const events = Array.isArray(h?.events) ? h.events : [];
            const mapped = events.map((e) => ({
              role: e.from === "user" ? "user" : "assistant",
              content: e.body || "",
            }));
            if (mapped.length) setMessages(mapped);
          } catch {}
        }
      } catch (e) {
        if (!cancelled) {
          const disabled = e && (e.code === "agent_disabled" || String(e.message).includes("agent_disabled"));
          setMessages([
            {
              role: "assistant",
              content: disabled ? translate("ChatInactive") : translate("ChatUnavailable"),
            },
          ]);
        }
      } finally {
        if (!cancelled) setStarting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantId, agentId, startRequested, email, translate]);

  // Scroll to bottom when messages or loading indicator changes
  useEffect(() => {
    try {
      endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    } catch {}
  }, [messages, loading]);

  const doSend = async () => {
    const text = input.trim();
    if (!text || !conversationId) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const resp = await sendMessage({
        tenantId,
        agentId,
        conversationId,
        body: text,
        imageUrls: [],
      });
      setMessages((m) => [...m, { role: "assistant", content: resp?.reply || "" }]);
    } catch (e) {
      const code = e?.code || "";
      if (code === "conversation_not_found" || code === "agent_not_found") {
        try {
          const key = `tm:conv:${tenantId}:${agentId}:${(email || "").trim().toLowerCase()}`;
          localStorage.removeItem(key);
        } catch {}
        setConversationId(null);
        setMessages((m) => [...m, { role: "assistant", content: translate("ChatUnavailable") }]);
      } else {
        setMessages((m) => [...m, { role: "assistant", content: translate("ChatUnavailable") }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const layout = isEmbed
    ? {
        outerPadding: 0,
        paperPadding: 1,
        paperMaxWidth: "100%",
        paperHeight: "100%",
        contentGap: 1,
        marketingMaxWidth: "100%",
        chatMaxWidth: "100%",
        chatHeight: "100%",
        footerMaxWidth: "100%",
      }
    : {
        outerPadding: 3,
        paperPadding: 2,
        paperMaxWidth: 960,
        paperHeight: "calc(100vh - 32px)",
        contentGap: 2,
        marketingMaxWidth: 720,
        chatMaxWidth: 720,
        chatHeight: "100%",
        footerMaxWidth: 720,
      };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: layout.outerPadding,
        height: isEmbed ? "100%" : "100vh",
        width: "100%",
        bgcolor: isEmbed ? "background.default" : "#f5f7fb",
        boxSizing: "border-box",
      }}
    >
      {!isEmbed && (
        <Helmet>
          <title>
            {agentMeta.name
              ? `${agentMeta.name} | Talkmate AI Agent`
              : "Talkmate | AI Agent Chat"}
          </title>
          <meta
            name="description"
            content={
              agentMeta.presentation
                ? agentMeta.presentation
                : "Chat with a Talkmate AI agent via public link or embed."
            }
          />
          <meta name="robots" content="index, follow" />
          <meta
            property="og:title"
            content={
              agentMeta.name
                ? `${agentMeta.name} | Talkmate AI Agent`
                : "Talkmate | AI Agent Chat"
            }
          />
          <meta
            property="og:description"
            content={
              agentMeta.presentation
                ? agentMeta.presentation
                : "Chat with a Talkmate AI agent via public link or embed."
            }
          />
          <link rel="canonical" href={typeof window !== "undefined" ? window.location.href : ""} />
        </Helmet>
      )}
      <Paper
        sx={{
          width: "100%",
          maxWidth: layout.paperMaxWidth,
          p: layout.paperPadding,
          display: "flex",
          flexDirection: "column",
          height: layout.paperHeight,
          maxHeight: layout.paperHeight,
          boxSizing: "border-box",
          bgcolor: isEmbed ? "background.paper" : "#ffffff",
          border: isEmbed ? undefined : "1px solid #e5e7eb",
          boxShadow: isEmbed ? undefined : "0 14px 36px rgba(15,23,42,0.08)",
          borderRadius: isEmbed ? undefined : 2,
        }}
      >
        {!isEmbed && (
          <Typography variant="h6" sx={{ mb: 1 }}>
            AI Agent
          </Typography>
        )}

        {!isEmbed && (hasShare || agentMeta.name || agentMeta.presentation) && (
          <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: layout.contentGap }}>
            <Box
              sx={{
                width: "100%",
                maxWidth: layout.marketingMaxWidth,
                borderRadius: 3,
                border: 1,
                borderColor: isEmbed ? "divider" : "#cbd5e1",
                p: 2.5,
                textAlign: "center",
                bgcolor: isEmbed
                  ? (theme) =>
                      theme.palette.mode === "dark" ? "rgba(124,77,255,0.15)" : "rgba(124,77,255,0.1)"
                  : "#dbeafe",
                boxShadow: isEmbed ? "0 12px 30px rgba(0,0,0,0.08)" : "0 16px 32px rgba(37,99,235,0.15)",
              }}
            >
              {agentMeta.name && (
                <Typography variant="h5" fontWeight={800} sx={{ mb: 0.8, color: "#0f172a" }}>
                  {agentMeta.name}
                </Typography>
              )}
              {agentMeta.presentation && (
                <Typography variant="body1" sx={{ color: "#0f172a" }}>
                  {agentMeta.presentation}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            gap: layout.contentGap,
          }}
        >
          {!conversationId && !starting && !startRequested && (
            <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2" sx={{ textAlign: "center", color: "#0f172a" }}>
                {translate("ChatEmailHint")}
              </Typography>
              <TextField
                size="small"
                type="email"
                placeholder={translate("WriteAMessageEmail")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  width: "100%",
                  maxWidth: 360,
                  alignSelf: "center",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#0f172a" },
                    "&:hover fieldset": { borderColor: "#0b152c" },
                    "&.Mui-focused fieldset": { borderColor: "#0b152c" },
                  },
                }}
              />
              <Box sx={{ display: "flex", gap: 1, mt: 0.5, justifyContent: "center" }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (email.trim()) setStartRequested(true);
                  }}
                >
                  {translate("Start")}
                </Button>
              </Box>
            </Box>
          )}

          {starting && !conversationId && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                {translate("InitializingChat")}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "stretch",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: layout.chatMaxWidth,
                height: "100%",
                border: 1,
                borderColor: isEmbed ? "divider" : "#0f172a",
                borderRadius: 1,
                p: isEmbed ? 1 : 1.5,
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
                mx: "auto",
                bgcolor: isEmbed ? (theme) => theme.palette.background.paper : "#ffffff",
              }}
            >
              {isEmbed && (
                <Box
                  sx={{
                    pb: 1,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {agentMeta.name || "AI Agent"}
                  </Typography>
                  {agentMeta.presentation && (
                    <Typography variant="caption" color="text.secondary">
                      {agentMeta.presentation}
                    </Typography>
                  )}
                </Box>
              )}

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  mt: isEmbed ? 1 : 0,
                }}
              >
                {messages.map((m, i) => {
                  const isUser = m.role === "user";
                  return (
                    <Box
                      key={i}
                      sx={{
                        alignSelf: isUser ? "flex-end" : "flex-start",
                        maxWidth: "85%",
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor: isUser ? "#f8fafc" : "#e8f0ff",
                        border: isEmbed
                          ? (theme) => `1px solid ${theme.palette.divider}`
                          : "1px solid #e5e7eb",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mb: 0.25, color: "#0f172a" }}
                      >
                        {isUser ? translate("You") : translate("Agent")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                          color: "#0f172a",
                        }}
                      >
                        {m.content}
                      </Typography>
                    </Box>
                  );
                })}
                {loading && conversationId && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      {translate("botIsWriting")}
                    </Typography>
                  </Box>
                )}
                <div ref={endRef} />
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                width: "100%",
                maxWidth: layout.footerMaxWidth,
                borderTop: isEmbed ? undefined : "1px solid #e5e7eb",
                pt: isEmbed ? 0 : 1,
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder={translate("WriteAMessage")}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doSend()}
                disabled={!conversationId || loading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#0f172a" },
                    "&:hover fieldset": { borderColor: "#0b152c" },
                    "&.Mui-focused fieldset": { borderColor: "#0b152c" },
                  },
                  "& .MuiOutlinedInput-input": { color: "#0f172a" },
                  input: { color: "#0f172a" },
                  borderColor: "#0f172a",
                }}
              />
              <Button onClick={doSend} disabled={!conversationId || loading} variant="contained">
                {translate("Send")}
              </Button>
              <Tooltip title={language === "fr" ? "FR" : "EN"}>
                <IconButton
                  size="small"
                  onClick={() => setLanguage(language === "fr" ? "en" : "fr")}
                  sx={{ border: 1, borderColor: "divider" }}
                >
                  <img
                    src={language === "fr" ? frFlag : ukFlag}
                    alt={language === "fr" ? "FR" : "EN"}
                    style={{ width: 20, height: 14, display: "block" }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">
              Powered by{" "}
              <a
                href="https://talk-mate-backend.web.app/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#1d4ed8", fontWeight: 700 }}
              >
                Talkmate
              </a>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export function PublicChatShare() {
  return <PublicChatBase variant="share" />;
}

export function PublicChatEmbed() {
  return <PublicChatBase variant="embed" />;
}

export default PublicChatShare;
